import { useRequest, fetcher } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  DialogHeader,
  Dialog,
  Modal,
  PrimaryButton,
  Space,
  Text,
  SuccessTag,
  Loader,
  GhostIconButton,
} from "@certn/cascadia-react";
import { NavigationBar } from "@certn/cascadia-react/components/navigation/navigation-top/NavigationBar";
import { formatCurrency, formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { UpdatePONumberModal } from "./UpdatePONumberModal";
import { InvoiceProps } from "../../../../../pdf-lambda/src/schemas-and-types";
import { StripePayment } from "../../../components/stripe/StripePayment";
import { useStripePaymentContext } from "../../../components/stripe/StripePaymentContext";
import { Schemas } from "../../../types";

interface Props {
  invoiceData?: InvoiceProps["invoiceData"];
  isPublic?: boolean;
  onPONumberUpdated?: () => void;
  onInvoiceUpdated?: () => void;
}

interface BeginPaymentIntentData {
  client_secret?: string;
  stripe_gateway?: string;
  written_off?: boolean;
}

export function InvoiceDetailPageHeader(props: Props) {
  const { invoiceData, onPONumberUpdated, onInvoiceUpdated } = props;
  const { invoiceId } = useParams();
  const { t } = useTranslation();
  const request = useRequest();
  const [clientSecret, setClientSecret] = useState<null | string>(null);
  const [stripeGateway, setStripeGateway] = useState<null | string>(null);
  const [showUpdatePONumberModal, setShowUpdatePONumberModal] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const { enableInvoicePayments } = useFlags();
  const { setState: setStripePaymentState, clearState } =
    useStripePaymentContext();
  const navigate = useNavigate();
  const openInvoiceInNewTab = async (invoiceId: string) => {
    try {
      const response = await request<Schemas["InvoicePdfResponse"]>(
        `/api/internal/billing/invoices/${invoiceId}/public-download-invoice/`,
        {
          method: "GET",
        }
      );
      window.open(response.invoice, "_blank");
    } catch {
      showToast(t("Unable to open invoice"), { type: "error" });
    }
  };

  const fetchClientSecret = async () => {
    if (clientSecret) {
      setPaymentOpen(true);
      return;
    }

    setStripePaymentState("fetching-client-secret");
    fetcher
      .request<BeginPaymentIntentData>(
        `/api/internal/billing/invoices/${invoiceId}/payment/begin/`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      )
      .then((data) => {
        if (data.written_off) {
          clearState();
          const closedAmount = formatCurrency(
            Number(invoiceData?.balance ?? invoiceData?.total ?? 0),
            undefined,
            invoiceData?.currency ?? "USD",
            2
          );
          showToast(
            t(
              "This balance of {{amount}} has been closed. No payment is required.",
              { amount: closedAmount }
            ),
            { type: "success" }
          );
          onInvoiceUpdated?.();
          return;
        }
        setClientSecret(data.client_secret ?? null);
        setStripeGateway(data.stripe_gateway ?? null);
        setStripePaymentState("idle");
        setPaymentOpen(true);
      })
      .catch((error) => {
        // TODO: Handle the error here. Make sure Sentry gets the error and local context like invoice id.
        console.error("Failed to fetch client secret to pay invoice", error);
        setStripePaymentState("error");
      });
  };

  if (!invoiceData) {
    return <Loader />;
  }

  const requiresPayment = invoiceData.status === "OPEN";

  return (
    <div className={styles.headerContainer}>
      <NavigationBar testID="invoice-detail-header-nav-bar">
        <GhostIconButton
          icon="arrow_back"
          size="medium"
          testID="close-invoice-button"
          onPress={() => navigate("/billing/invoices")}
        />

        <div className={styles.headerTitle}>
          <Text as="h1" style="global/title-large">
            <Trans>Invoice - </Trans>
            {formatDate(String(invoiceData.issue_date), "MMMM yyyy")}
          </Text>
          {requiresPayment && (
            <SuccessTag>
              <Trans>Payment required</Trans>
            </SuccessTag>
          )}
        </div>

        <Space />
        {enableInvoicePayments && requiresPayment && (
          <PrimaryButton
            size="medium"
            testID="save-and-exit-button"
            onPress={() => fetchClientSecret()}
          >
            <Trans>Pay invoice</Trans>
          </PrimaryButton>
        )}
        {enableInvoicePayments && paymentOpen && (
          <Modal isOpen={paymentOpen} onOpenChange={setPaymentOpen}>
            <Dialog>
              {() => (
                <>
                  <DialogHeader>{t("Payment")}</DialogHeader>
                  <StripePayment
                    clientSecret={clientSecret}
                    stripeGateway={stripeGateway}
                  />
                </>
              )}
            </Dialog>
          </Modal>
        )}

        {invoiceData.status !== "VOIDED" && (
          <BasicButton
            aria-label="Update PO number"
            testID="update-po-number-button"
            onPress={() => setShowUpdatePONumberModal(true)}
          >
            {t("Update PO number")}
          </BasicButton>
        )}

        <BasicButton
          leftIcon="download"
          aria-label="Download PDF"
          testID="download-pdf-button"
          onPress={() => openInvoiceInNewTab(invoiceId || "")}
        >
          {t("Download PDF")}
        </BasicButton>
      </NavigationBar>
      {showUpdatePONumberModal && (
        <UpdatePONumberModal
          invoiceId={invoiceId || ""}
          currentPONumber={
            invoiceData.billing_profile.purchase_order_number || ""
          }
          onClose={() => setShowUpdatePONumberModal(false)}
          onSuccess={onPONumberUpdated}
        />
      )}
    </div>
  );
}

const styles = {
  headerContainer: css({
    marginBottom: "var(--space-5)",
  }),
  headerTitle: css({
    marginLeft: "var(--space-2)",
    display: "flex",
    flexDirection: "column",
  }),
};
